import React, { useEffect, useState } from "react";
import { Box, styled, Typography, Button, Grid2, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getColumnsConfig } from "../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { usePagesContent } from "../../../../Hooks/usePagesContent";
import AppCache from "../../../../Helper/Common/AppCache";
import { fetchFiltersDataObject } from "../../../../Helper/Table/TableHelper";
import {
  convertAndGetTimeStampInRequiredFormat,
  convertZonetoUTCEnd,
  convertZonetoUTCStart,
  getDatePickerDate,
} from "../../../../Helper/DatePicker/DateConverter";
import { useBatteryInfo, useVehicleInfo } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import { decryptTheParams, encryptTheParams } from "../../../../Helper/QueryParams/EncryptDecrypt";
import GenericDraggableTableView from "../../../../Features/Table/GenericDraggableTable";
import { MAX_COLUMN_SELECTION_COUNT } from "../../../../Helper/DigitalTwin/DataFile";
import {
  alertListAction,
  getBatteryAlertIncidents,
} from "../../../../Api/BmsFaultsApi";
import AlertActionDialog from "../../../BmsFaults/AlertActionDialog";
import { PrimaryTooltip } from "../../../../Components/StyledComponent";
import { usePermissions } from "../../../../Context/PermissionContext";
import { FEATURE_CODES } from "../../../../Static/FeatureMapping";
import CustomIcon from "../../../../Components/CustomIcon";
import SingleAlertAnalysisDialog from "../../../BmsFaults/SingleAlertAnalysis/SingleAlertAnalysisDialog";
import dayjs from "dayjs";
import { isOrganizationJBM } from "../../../../Helper/Organization/Functions";
import { withHeaderPrimaryEntity } from "../../../AdvancedAnalytics/Common/advancedAnalysisNav";
import { useNavigate } from "react-router-dom";

const TableContainer = styled(Box)(() => ({
  minHeight: "200px",
}));

const StyledAction = styled(Button)(() => ({
  padding: '4px 12px',
  backgroundColor: '#FFFFFF',
  border: '1px solid #E6E6E6',
  borderRadius: '8px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  textTransform: 'none',
  minWidth: 0,

  '&:hover': {
    border: '1px solid #6D72F6',
    '& span': { color: '#6D72F6' },
    '& svg path': { fill: '#6D72F6' },
  }
}));

const TABLE_ID = "BatteryBmsFaults";

const defaultStartDate = convertZonetoUTCStart(getDatePickerDate(undefined, -90));
const defaultEndDate = convertZonetoUTCEnd(getDatePickerDate(undefined, 0));

const getDefaultFilters = (queryParams) => {
  try {
    let queryFilters = queryParams?.bmsFaultFilters || "[]";
    queryFilters = JSON.parse(queryFilters);
    if (queryFilters && Object.keys(queryFilters).length > 0) {
      return queryFilters;
    }
  } catch (error) {
    console.error("CustomBatteryBmsFaults: error parsing filters:", error);
  }
  return {};
};

/**
 * Custom platform BMS Faults table.
 *
 * Reads customAlertActions from metaInfo (set by buildCustomMetaInfo in
 * CustomDigitalTwinPage) to control which action buttons appear per row:
 *   "analyse"          → Analyse binocular button
 *   "advanced-analysis"→ Advanced Analysis settings button
 *   "viewDetails"      → View Details / Act Now / Update CTA button
 *
 * All three are shown when customAlertActions is empty (default behaviour).
 */
const CustomBatteryBmsFaults = ({ batteryID }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "generic_table"]);
  const { batteryInfo } = useBatteryInfo();
  const vehicleInfo = useVehicleInfo();
  const pageContent = usePagesContent();
  const { hasPermission, isRbacV2Enabled } = usePermissions();
  const canUpdate = !isRbacV2Enabled() || hasPermission(FEATURE_CODES.BMS_FAULTS_V2, 'update');

  // Read allowed actions from org meta; fall back to all three
  const allowedActions = pageContent?.metaInfo?.customAlertActions?.length
    ? pageContent.metaInfo.customAlertActions
    : ['analyse', 'advanced-analysis', 'viewDetails'];

  const [selectedColumns, setSelectedColumns] = useState([]);
  const [defaultSelectedColumns, setDefaultSelectedColumns] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [currentTableColumns, setCurrentTableColumns] = useState([]);
  const [availableFilters, setAvailableFilters] = useState({});
  const [selectedFilters, setSelectedFilters] = useState(getDefaultFilters(decryptTheParams()));
  const [selectedRowKeys, setSelectedRowKeys] = useState({});
  const [tableData, setTableData] = useState([]);
  const [pageInfo, setPageInfo] = useState({});
  const [unfilteredTotal, setUnfilteredTotal] = useState(null);
  const [sorting, setSorting] = useState([
    { sortBy: "bmsAlertStatus", order: "asc" },
    { sortBy: "severityLevelID", order: "desc" },
    { sortBy: "customerDBTimeInsert", order: "desc" },
  ]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
  const [openAnalyseDialog, setOpenAnalyseDialog] = useState(false);
  const [analyseDialogData, setAnalyseDialogData] = useState({});
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [actionDialogData, setActionDialogData] = useState({});

  const pageCount = 20;

  const handleAnalyseClick = (alertIncident) => {
    const severityWithoutLV = alertIncident.severityLevel?.replace(/^LV/, '') || alertIncident.severityLevel;
    setAnalyseDialogData({
      battery: { ...batteryInfo, ...vehicleInfo },
      alertIncident: { ...alertIncident, severityLevel: severityWithoutLV },
    });
    setOpenAnalyseDialog(true);
  };

  const handleAdvancedAnalyticsClick = (alertIncident) => {
    const events = alertIncident?.incidentErrorName ? [alertIncident.incidentErrorName] : [];
    const startDate = alertIncident?.customerDBTimeInsert
      ? convertAndGetTimeStampInRequiredFormat(alertIncident.customerDBTimeInsert, "YYYY-MM-DD")
      : null;
    let endDate;
    if (alertIncident?.resolveCustomerDBTime) {
      endDate = convertAndGetTimeStampInRequiredFormat(alertIncident.resolveCustomerDBTime, "YYYY-MM-DD");
    } else {
      endDate = dayjs().format("YYYY-MM-DD");
    }
    let temp = {
      batteryID: batteryInfo?.batteryID,
      deviceID: batteryInfo?.deviceID,
      selectedType: 'deviceID',
      selectedTypeId: batteryInfo?.deviceID,
      events: JSON.stringify(events),
      startDate,
      endDate,
      entityAffectedType: alertIncident?.entityAffected || 'battery',
      entityAffectedId: alertIncident?.entityAffectedID,
      category: alertIncident?.category || null,
    };
    if (isOrganizationJBM() && batteryInfo?.licensePlateNo) {
      temp = { ...temp, selectedType: 'vehicle', selectedTypeId: batteryInfo.licensePlateNo };
    }
    // Default to the header identifier when this page's meta configures one.
    temp = withHeaderPrimaryEntity(temp, {
      primaryDetails: pageContent?.metaInfo?.header?.title?.primaryDetails,
      vehicleInfo,
      batteryInfo,
    });
    encryptTheParams(temp, navigate, false, '/advanced-analysis', true);
  };

  const handleActionClick = (alertIncident) => {
    const severityWithoutLV = alertIncident.severityLevel?.replace(/^LV/, '') || alertIncident.severityLevel;
    setActionDialogData({
      battery: { ...batteryInfo, ...vehicleInfo },
      alertIncident: { ...alertIncident, severityLevel: severityWithoutLV },
    });
    setOpenActionDialog(true);
  };

  const customRenderers = {
    bms_fault_action: (record) => {
      let actionCtaLabel = 'View Details';
      if (canUpdate) {
        const bmsAlertStatusKey = record.bmsAlertStatus?.toLowerCase();
        if (bmsAlertStatusKey === 'open') {
          actionCtaLabel = 'Act Now';
        } else if (!['resolved', 'force resolved', 'auto resolved'].includes(bmsAlertStatusKey)) {
          actionCtaLabel = 'Update';
        }
      }

      const showAnalyse = allowedActions.includes('analyse');
      const showAdvancedAnalysis = allowedActions.includes('advanced-analysis');
      const showStatus = allowedActions.includes('viewDetails');

      const visibleActions = [];
      if (showAnalyse) {
        visibleActions.push(
          <PrimaryTooltip key="analyse" placement="bottom" title="Analyse">
            <StyledAction onClick={() => handleAnalyseClick(record)} sx={{ py: '7px', px: 1 }}>
              <CustomIcon name="binocular-icon" fill='#494949' />
            </StyledAction>
          </PrimaryTooltip>
        );
      }
      if (showAdvancedAnalysis) {
        visibleActions.push(
          <PrimaryTooltip key="advanced" placement="bottom" title="Advanced Analysis">
            <StyledAction onClick={() => handleAdvancedAnalyticsClick(record)} sx={{ py: '7px', px: 1 }}>
              <CustomIcon name='advanced-analysis-settings' fill="#494949" />
            </StyledAction>
          </PrimaryTooltip>
        );
      }
      if (showStatus) {
        visibleActions.push(
          <StyledAction key="status" onClick={() => handleActionClick(record)}>
            <Typography variant="fw500s14c494949" sx={{ lineHeight: '20px' }}>
              {actionCtaLabel}
            </Typography>
          </StyledAction>
        );
      }

      return (
        <Grid2 container alignItems={'center'} gap={1.5}>
          {visibleActions.map((action, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4", my: 0.75 }} />}
              {action}
            </React.Fragment>
          ))}
        </Grid2>
      );
    },
  };

  const formatIncidentData = (data) => {
    return data.map((incident) => ({
      ...incident,
      id: incident.incidentID,
      severityLevel: incident.severityLevel ? `LV${incident.severityLevel}` : "",
    }));
  };

  const fetchDigitalTwinAlerts = async () => {
    try {
      setIsLoading(true);
      const defaultFilters = { batteryID: [batteryID] };
      const activatedAtFilter = selectedFilters.activatedAt ? JSON.parse(selectedFilters.activatedAt) : {};
      const startDate = activatedAtFilter.startDateUTC || defaultStartDate;
      const endDate = activatedAtFilter.endDateUTC || defaultEndDate;
      const filters = {
        ...selectedFilters,
        ...defaultFilters,
        pageCount: pageCount,
        pageNum: pageNumber,
        sortBy: JSON.stringify(sorting),
      };
      const responseData = await getBatteryAlertIncidents({}, batteryID, filters);
      const data = responseData.response.data || [];
      const pageInfo = responseData.response.total || {};
      setPageInfo(pageInfo);
      const hasActiveFilters = selectedFilters && Object.keys(selectedFilters).length > 0;
      if (!hasActiveFilters) {
        setUnfilteredTotal(pageInfo.total ?? null);
      }
      setTableData(formatIncidentData(data));
      setIsLoading(false);
    } catch (error) {
      console.error("CustomBatteryBmsFaults: error fetching alerts:", error);
      setIsLoading(false);
    }
  };

  const applySelectedColumns = (newSelectedColumns) => {
    AppCache.setItem(TABLE_ID, { selectedColumns: newSelectedColumns });
    setSelectedColumns(newSelectedColumns);
  };

  const applySelectedFilters = (filters) => setSelectedFilters(filters);

  const onPageChange = (pageNo) => setPageNumber(pageNo);

  const getTotalRowSelection = () => Array.from(totalSelectedRowKeys);

  const getTotalRowSelectionCount = () => {
    if (isSelectAll) return pageInfo?.total;
    return totalSelectedRowKeys.size;
  };

  const getCurrentPageSelection = () => Array.from(totalSelectedRowKeys);

  const onSelectAllChange = (selected, selectedRows) => {
    setIsSelectAll(selected);
    if (selected) {
      setTotalSelectedRowKeys(new Set(selectedRows.map((row) => row?.id)));
      return;
    }
    setTotalSelectedRowKeys(new Set());
  };

  const onSelectChange = (record, selected) => {
    const updatedSelectedKeys = new Set(totalSelectedRowKeys);
    if (selected) {
      updatedSelectedKeys.add(record.id);
    } else {
      updatedSelectedKeys.delete(record.id);
      if (isSelectAll) setIsSelectAll(false);
    }
    setTotalSelectedRowKeys(updatedSelectedKeys);
  };

  const applySort = (key, order) => setSorting([{ sortBy: key, order }]);

  const formatColumnConfigsForCSV = (coulmnConfigs) => {
    return coulmnConfigs.map((cConfig) => {
      if (cConfig.dataIndex.toLowerCase() === "resolutiontime") {
        return { ...cConfig, headerLabel: "Resolution Time (Minutes)" };
      }
      return { ...cConfig };
    });
  };

  const getCsvColumnParams = () => {
    const csvColumnConfigs = formatColumnConfigsForCSV(currentTableColumns);
    return {
      columnOrder: csvColumnConfigs.map((column) => column.headerLabel),
      columns: JSON.stringify(csvColumnConfigs),
    };
  };

  const getCSVFileName = () => {
    return `BatteryBMSFaults_${batteryInfo.deviceID}_${dayjs().format("MMMM D, YYYY")}.csv`;
  };

  const prepareActionData = () => {
    const { activatedAt: selectedActivatedAt, ...restFilters } = selectedFilters;
    const filters = {
      ...restFilters,
      ...(selectedActivatedAt ? { activatedAt: Array.isArray(selectedActivatedAt) ? selectedActivatedAt[0] : selectedActivatedAt } : {}),
      batteryID: [batteryID],
      errorStatus: ["resolved", "active"],
    };
    const { columns, columnOrder: csvColumnOrder } = getCsvColumnParams();
    return {
      resourceIDs: isSelectAll ? [] : getTotalRowSelection(),
      filters: JSON.stringify(filters),
      columns,
      columnOrder: csvColumnOrder,
    };
  };

  const getCSVData = async () => {
    const actionData = prepareActionData();
    const res = await alertListAction({}, { ...actionData, actionType: "download-csv" });
    return res?.response?.data || [];
  };

  const setupTableColumns = () => {
    const tableInfo = pageContent?.metaInfo?.table?.bmsFaultsTable || {};
    const tableColumns = tableInfo?.columnsConfig || {};
    const tableColumnOrder = tableInfo?.columnOrdering || [];
    const availableColumns = getColumnsConfig({ tableColumns, tableColumnOrder, customRenderers, t });
    setAvailableColumns(availableColumns);
    const initalSelectedColumns = Object.keys(tableColumns) || [];
    const defaultColumns = initalSelectedColumns
      .sort((a, b) => {
        if (a.fixed && !b.fixed) return -1;
        if (!a.fixed && b.fixed) return 1;
        return 0;
      })
      .slice(0, MAX_COLUMN_SELECTION_COUNT);
    setSelectedColumns(defaultColumns);
    setDefaultSelectedColumns(defaultColumns);
    const currentTableColumns = availableColumns.filter((column) => initalSelectedColumns.includes(column.key));
    setCurrentTableColumns(currentTableColumns);
    setColumnOrder(currentTableColumns.map((column) => column.headerLabel));
  };

  const setupTableFilters = async () => {
    const tableFilters = pageContent?.metaInfo?.filterConfigurations?.bmsFaultsTable?.filters || [];
    const filtersObj = await fetchFiltersDataObject(tableFilters, availableFilters);
    if (filtersObj?.hierarchy?.values && !filtersObj.hierarchy.values.some(v => v.id === 'tms')) {
      filtersObj.hierarchy.values.push({ id: 'tms', value: 'TMS' });
    }
    const sortedFiltersObj = Object.fromEntries(
      Object.entries(filtersObj).sort(([, a], [, b]) => a.label.localeCompare(b.label))
    );
    setAvailableFilters(sortedFiltersObj);
  };

  const onColumnOrderChange = (tableColumns) => {
    setColumnOrder(tableColumns.map((value) => value.headerLabel));
  };

  const getCountText = () => {
    const displayTotal = unfilteredTotal !== null ? unfilteredTotal : pageInfo.total;
    if (!displayTotal) return "";
    return displayTotal;
  };

  const getFilteredCount = () => {
    if (!selectedFilters || Object.keys(selectedFilters).length < 1) return false;
    return pageInfo.total ? pageInfo.total : 0;
  };

  const getNoDataMessage = () => {
    if (!selectedFilters || Object.keys(selectedFilters) < 1) return "No issues found in this battery";
    return "No matches found for filters applied";
  };

  useEffect(() => {
    setupTableColumns();
    setupTableFilters();
  }, [pageContent]);

  useEffect(() => {
    const currentTableColumns = availableColumns.filter((column) => selectedColumns.includes(column.key));
    setCurrentTableColumns(currentTableColumns);
  }, [selectedColumns]);

  useEffect(() => {
    pageNumber === 1 ? fetchDigitalTwinAlerts() : onPageChange(1);
  }, [selectedFilters, sorting]);

  useEffect(() => {
    fetchDigitalTwinAlerts();
  }, [pageNumber]);

  return (
    <>
      <TableContainer>
        <GenericDraggableTableView
          tableID={"BatteryBmsFaults"}
          title={"Battery System Faults"}
          count={getCountText()}
          filteredCount={getFilteredCount()}
          rowsPerPage={pageCount}
          showPrimaryButton={false}
          disableSearch={true}
          getCSVData={getCSVData}
          getCSVFileName={getCSVFileName}
          showEmailComposer={false}
          filters={availableFilters}
          selectedFilters={selectedFilters}
          applyFilters={(args) => applySelectedFilters(args)}
          tableColumns={currentTableColumns}
          initialColumns={availableColumns}
          selectedColumns={selectedColumns}
          applySelectedColumns={applySelectedColumns}
          tableData={tableData}
          onColumnOrderChange={onColumnOrderChange}
          columnOrder={columnOrder}
          getCsvColumnSettings={getCsvColumnParams}
          isLoading={isLoading}
          pageNumber={pageNumber}
          total={pageInfo.total}
          applySort={applySort}
          onPageChange={onPageChange}
          emptyMessage={getNoDataMessage()}
          setSelectedRowKeys={setSelectedRowKeys}
          getTotalRowSelection={getTotalRowSelection}
          getTotalRowSelectionCount={getTotalRowSelectionCount}
          onClearSelection={() => {
            setTotalSelectedRowKeys(new Set());
            setIsSelectAll(false);
          }}
          rowSelection={{
            selectedRowKeys: getCurrentPageSelection(),
            onSelect: onSelectChange,
            onSelectAll: onSelectAllChange,
          }}
          maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
          defaultSelectedColumns={defaultSelectedColumns}
        />
      </TableContainer>
      <AlertActionDialog
        actionData={actionDialogData}
        open={openActionDialog}
        onClose={(hasUpdatedData) => {
          setOpenActionDialog(false);
          if (hasUpdatedData) fetchDigitalTwinAlerts();
        }}
      />
      <SingleAlertAnalysisDialog
        analyseData={analyseDialogData}
        open={openAnalyseDialog}
        onClose={() => setOpenAnalyseDialog(false)}
      />
    </>
  );
};

export default CustomBatteryBmsFaults;
